/**
 * Configuration options for dynamic rotation animation.
 */
export interface DynamicRotationConfig {
  /**
   * Base rotation speed in degrees per second.
   * @default 90 (one full rotation every 4 seconds)
   */
  baseSpeed?: number;

  /**
   * Duration of one speed variation cycle in seconds.
   * @default 3
   */
  speedCycleDuration?: number;

  /**
   * Amplitude of the primary speed variation (0 to 1).
   * Higher values create more dramatic speed changes.
   * @default 0.7
   */
  speedVariationAmplitude?: number;

  /**
   * Minimum speed multiplier to ensure continuous motion.
   * Set to 0 to allow stopping, or higher for faster minimum speed.
   * @default 0.3
   */
  minSpeedMultiplier?: number;

  /**
   * Whether to include secondary oscillation for more organic feel.
   * @default true
   */
  useSecondaryOscillation?: boolean;

  /**
   * Amplitude of secondary oscillation.
   * @default 0.3
   */
  secondaryAmplitude?: number;

  /**
   * Frequency multiplier for secondary oscillation relative to primary.
   * @default 0.37
   */
  secondaryFrequencyMultiplier?: number;

  /**
   * Amplitude of subtle rotation oscillation in degrees.
   * Set to 0 to disable.
   * @default 5
   */
  oscillationAmplitude?: number;

  /**
   * Frequency of the oscillation effect.
   * @default 0.02
   */
  oscillationFrequency?: number;

  /**
   * Phase offset based on element index for stagger effect.
   * @default 0.5
   */
  indexPhaseOffset?: number;

  /**
   * Secondary phase offset based on element index.
   * @default 1.2
   */
  secondaryIndexPhaseOffset?: number;

  /**
   * Direction of rotation: 1 for clockwise, -1 for counter-clockwise.
   * @default 1
   */
  direction?: 1 | -1;
}

const DEFAULT_CONFIG: Required<DynamicRotationConfig> = {
  baseSpeed: 90,
  speedCycleDuration: 3,
  speedVariationAmplitude: 0.7,
  minSpeedMultiplier: 0.3,
  useSecondaryOscillation: true,
  secondaryAmplitude: 0.3,
  secondaryFrequencyMultiplier: 0.37,
  oscillationAmplitude: 5,
  oscillationFrequency: 0.02,
  indexPhaseOffset: 0.5,
  secondaryIndexPhaseOffset: 1.2,
  direction: 1,
};

/**
 * Calculates a continuously varying rotation with dynamic speed.
 * Uses sine wave modulation to create organic speed variation.
 *
 * @param frame - Current animation frame
 * @param fps - Frames per second
 * @param index - Element index for stagger effect (default: 0)
 * @param config - Optional configuration to customize the animation
 * @returns Rotation angle in degrees
 */
export const calculateDynamicRotation = (
  frame: number,
  fps: number,
  index: number = 0,
  config: DynamicRotationConfig = {},
): number => {
  const {
    baseSpeed,
    speedCycleDuration,
    speedVariationAmplitude,
    minSpeedMultiplier,
    useSecondaryOscillation,
    secondaryAmplitude,
    secondaryFrequencyMultiplier,
    oscillationAmplitude,
    oscillationFrequency,
    indexPhaseOffset,
    secondaryIndexPhaseOffset,
    direction,
  } = { ...DEFAULT_CONFIG, ...config };

  // Base rotation speed (degrees per frame)
  const baseRotationSpeed = baseSpeed / fps;

  // Time-based phase for speed variation
  const speedCycleFrequency = (2 * Math.PI) / (fps * speedCycleDuration);
  const speedPhase = frame * speedCycleFrequency + index * indexPhaseOffset;

  // Speed multiplier oscillates between minSpeedMultiplier and (1 + speedVariationAmplitude)
  const centerSpeed =
    (1 + minSpeedMultiplier) / 2 + speedVariationAmplitude / 2;
  const speedMultiplier =
    centerSpeed + speedVariationAmplitude * Math.sin(speedPhase);

  // Secondary oscillation for more organic feel
  let secondaryVariation = 0;
  if (useSecondaryOscillation) {
    const secondaryPhase =
      frame * speedCycleFrequency * secondaryFrequencyMultiplier +
      index * secondaryIndexPhaseOffset;
    secondaryVariation = secondaryAmplitude * Math.sin(secondaryPhase);
  }

  // Calculate cumulative rotation
  const effectiveSpeed =
    baseRotationSpeed * (speedMultiplier + secondaryVariation);
  const rotation = frame * effectiveSpeed * direction;

  // Add subtle oscillation to the rotation itself
  const oscillation =
    oscillationAmplitude * Math.sin(frame * oscillationFrequency + index);

  return rotation + oscillation;
};
